import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { leadsData, getScoreCategory, getScoreBadgeColors, getScoreLabel, stageBadgeColors } from "@/mocks/leadsData";
import { callsData } from "@/mocks/callsData";
import { quotationsData, statusBadgeColors as quotationStatusColors } from "@/mocks/quotationsData";
import type { Lead } from "@/mocks/leadsData";
import type { CallRecord } from "@/mocks/callsData";
import type { Quotation } from "@/mocks/quotationsData";

const quickActions = [
  { icon: "ri-add-line", color: "text-indigo-600", bg: "bg-indigo-100", label: "Add New Lead", path: "/leads", action: "add-lead" },
  { icon: "ri-phone-line", color: "text-green-600", bg: "bg-green-100", label: "Log a Call", path: "/calls", action: "log-call" },
  { icon: "ri-calendar-line", color: "text-amber-600", bg: "bg-amber-100", label: "Schedule Follow-up", path: "/calls", action: "scheduled" },
  { icon: "ri-bar-chart-2-line", color: "text-purple-600", bg: "bg-purple-100", label: "View Pipeline", path: "/pipeline" },
];

const MAX_LEADS = 4;
const MAX_CALLS = 3;
const MAX_CONTACTS = 3;
const MAX_QUOTATIONS = 4;
const MAX_PROJECTS = 4;

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [liveQuotations, setLiveQuotations] = useState<Quotation[]>([]);
  const [liveProjects, setLiveProjects] = useState<any[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([
    "Vikram Mehta",
    "IndiaMart leads",
    "Site visit",
  ]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch("/api/v1/quotations")
      .then((r) => r.json())
      .then((res) => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        if (list.length > 0) setLiveQuotations(list);
      })
      .catch(() => {});

    fetch("/api/v1/projects")
      .then((r) => r.json())
      .then((res) => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        if (list.length > 0) setLiveProjects(list);
      })
      .catch(() => {});
  }, []);

  // Search results
  const results = useMemo(() => {
    if (!query.trim()) return { leads: [] as Lead[], calls: [] as CallRecord[], contacts: [] as Lead[], quotations: [] as Quotation[], projects: [] as any[] };

    const q = query.toLowerCase();

    const matchedLeads = leadsData.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.company.toLowerCase().includes(q) ||
        l.phone.includes(q) ||
        l.email.toLowerCase().includes(q) ||
        l.source.toLowerCase().includes(q) ||
        l.stage.toLowerCase().includes(q) ||
        l.assigned.toLowerCase().includes(q)
    );

    const matchedCalls = callsData.filter(
      (c) =>
        c.leadName.toLowerCase().includes(q) ||
        c.agent.toLowerCase().includes(q) ||
        c.phone.includes(q)
    );

    // Contacts are leads with phone/email, shown differently
    const matchedContacts = matchedLeads.filter((l) => l.phone || l.email);

    // Quotations — match by quote number, lead name, lead company, or agent
    const quotationsPool = liveQuotations.length > 0 ? liveQuotations : quotationsData;
    const matchedQuotations = quotationsPool.filter(
      (qt) =>
        (qt.quoteNo && qt.quoteNo.toLowerCase().includes(q)) ||
        ((qt as any).quote_number && (qt as any).quote_number.toLowerCase().includes(q)) ||
        (qt.leadName && qt.leadName.toLowerCase().includes(q)) ||
        ((qt as any).client_name && (qt as any).client_name.toLowerCase().includes(q)) ||
        (qt.leadCompany && qt.leadCompany.toLowerCase().includes(q)) ||
        ((qt as any).client_company && (qt as any).client_company.toLowerCase().includes(q)) ||
        (qt.agent && qt.agent.toLowerCase().includes(q))
    );

    // Projects — match by name, client name, description, category
    const matchedProjects = liveProjects.filter(
      (p) =>
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.client_name && p.client_name.toLowerCase().includes(q)) ||
        (p.description && p.description.toLowerCase().includes(q)) ||
        (p.category && p.category.toLowerCase().includes(q))
    );

    return {
      leads: matchedLeads.slice(0, MAX_LEADS),
      calls: matchedCalls.slice(0, MAX_CALLS),
      contacts: matchedContacts.slice(0, MAX_CONTACTS),
      quotations: matchedQuotations.slice(0, MAX_QUOTATIONS),
      projects: matchedProjects.slice(0, MAX_PROJECTS),
    };
  }, [query, liveQuotations, liveProjects]);

  // Flatten all result items for keyboard navigation
  const flatResults = useMemo(() => {
    const items: { type: "header" | "lead" | "call" | "contact" | "quotation" | "project" | "action"; label?: string; data?: any; actionKey?: string }[] = [];

    const isSearching = query.trim().length > 0;

    if (isSearching) {
      if (results.projects.length > 0) {
        items.push({ type: "header", label: "PROJECTS" });
        results.projects.forEach((p) => items.push({ type: "project", data: p }));
      }
      if (results.leads.length > 0) {
        items.push({ type: "header", label: "LEADS" });
        results.leads.forEach((l) => items.push({ type: "lead", data: l }));
      }
      if (results.calls.length > 0) {
        items.push({ type: "header", label: "CALLS" });
        results.calls.forEach((c) => items.push({ type: "call", data: c }));
      }
      if (results.contacts.length > 0) {
        items.push({ type: "header", label: "CONTACTS" });
        results.contacts.forEach((c) => items.push({ type: "contact", data: c }));
      }
      if (results.quotations.length > 0) {
        items.push({ type: "header", label: "QUOTATIONS" });
        results.quotations.forEach((qt) => items.push({ type: "quotation", data: qt }));
      }
    } else {
      // Recent searches
      items.push({ type: "header", label: "RECENT SEARCHES" });
      recentSearches.forEach((s) => items.push({ type: "action", label: s, actionKey: `recent-${s}` }));
      // Quick actions
      items.push({ type: "header", label: "QUICK ACTIONS" });
      quickActions.forEach((qa) => items.push({ type: "action", label: qa.label, actionKey: qa.action || qa.path }));
    }

    return items;
  }, [results, recentSearches, query]);

  const totalItems = flatResults.length;

  // Reset highlight when results change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [query]);

  // ⌘K / Ctrl+K global shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setFocused(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!focused) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setFocused(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [focused]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!focused) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          let next = prev + 1;
          // Skip headers
          while (next < totalItems && flatResults[next]?.type === "header") {
            next++;
          }
          return next >= totalItems ? 0 : next;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          let next = prev - 1;
          while (next >= 0 && flatResults[next]?.type === "header") {
            next--;
          }
          return next < 0 ? totalItems - 1 : next;
        });
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < totalItems) {
          const item = flatResults[highlightedIndex];
          handleSelect(item);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setFocused(false);
        inputRef.current?.blur();
      }
    },
    [focused, highlightedIndex, flatResults, totalItems]
  );

  const handleSelect = useCallback(
    (item: (typeof flatResults)[number]) => {
      if (!item) return;

      if (item.type === "project" && item.data && "id" in item.data) {
        const project = item.data;
        setQuery(project.name);
        const updated = [project.name, ...recentSearches.filter((s) => s !== project.name)].slice(0, 5);
        setRecentSearches(updated);
        setFocused(false);
        navigate(`/projects/${project.id}`);
      } else if (item.type === "lead" && item.data && "id" in item.data) {
        const lead = item.data as Lead;
        setQuery(lead.name);
        const updated = [lead.name, ...recentSearches.filter((s) => s !== lead.name)].slice(0, 5);
        setRecentSearches(updated);
        setFocused(false);
        navigate(`/leads/${lead.id}`);
      } else if (item.type === "call" && item.data) {
        const call = item.data as CallRecord;
        setQuery(call.leadName);
        setFocused(false);
        navigate("/calls");
      } else if (item.type === "contact" && item.data && "id" in item.data) {
        const lead = item.data as Lead;
        setQuery(lead.name);
        const updated = [lead.name, ...recentSearches.filter((s) => s !== lead.name)].slice(0, 5);
        setRecentSearches(updated);
        setFocused(false);
        navigate(`/leads/${lead.id}`);
      } else if (item.type === "quotation" && item.data) {
        const quotation = item.data as Quotation;
        setQuery(quotation.quoteNo);
        const updated = [quotation.quoteNo, ...recentSearches.filter((s) => s !== quotation.quoteNo)].slice(0, 5);
        setRecentSearches(updated);
        setFocused(false);
        navigate(`/quotations/${quotation.id}`);
      } else if (item.type === "action") {
        if (item.actionKey && item.actionKey.startsWith("recent-")) {
          const term = item.label!;
          setQuery(term);
          inputRef.current?.focus();
        } else if (item.actionKey === "add-lead") {
          setFocused(false);
          navigate("/leads");
          // Dispatch custom event so leads page can open add modal
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("open-add-lead-modal"));
          }, 100);
        } else if (item.actionKey === "log-call") {
          setFocused(false);
          navigate("/calls");
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent("open-log-call-modal"));
          }, 100);
        } else if (item.actionKey === "scheduled") {
          setFocused(false);
          navigate("/calls?tab=scheduled");
        } else {
          setFocused(false);
          navigate(`/${item.actionKey}`);
        }
      }
    },
    [navigate, recentSearches]
  );

  const handleFocus = () => setFocused(true);

  const handleClear = () => {
    setQuery("");
    inputRef.current?.focus();
  };

  const handleRemoveRecent = (term: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRecentSearches((prev) => prev.filter((s) => s !== term));
  };

  const showDropdown = focused;
  const isSearching = query.trim().length > 0;
  const hasResults = isSearching && (results.leads.length > 0 || results.calls.length > 0 || results.contacts.length > 0 || results.quotations.length > 0);
  const showNoResults = isSearching && !hasResults;

  return (
    <div ref={containerRef} className="relative flex-1 max-w-md mx-auto hidden md:block">
      {/* Search input */}
      <div
        className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm transition-all duration-200 border cursor-text ${
          focused
            ? "bg-white border-indigo-300 ring-1 ring-indigo-100"
            : "bg-gray-100 border-transparent"
        }`}
        onClick={() => inputRef.current?.focus()}
      >
        <i className="ri-search-line text-gray-400 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setFocused(true); }}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder="Search leads, calls, contacts..."
          className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400 min-w-0"
        />
        {query ? (
          <button
            onClick={handleClear}
            className="flex-shrink-0 p-0.5 rounded hover:bg-gray-200 transition-colors"
          >
            <i className="ri-close-line text-gray-400 text-xs" />
          </button>
        ) : (
          <span className="text-[11px] text-gray-400 flex-shrink-0 border border-gray-300 rounded px-1.5 py-0.5 leading-none font-medium">
            ⌘K
          </span>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 top-full mt-2 bg-white rounded-xl border border-gray-200 shadow-lg z-50 overflow-hidden"
        >
          {/* No results state */}
          {showNoResults && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <i className="ri-search-line text-4xl text-gray-200 mb-3" />
              <p className="text-sm text-gray-700 font-medium">
                No results for &apos;{query}&apos;
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Try searching by name, phone, email or company
              </p>
            </div>
          )}

          {/* Empty state: recent + quick actions */}
          {!isSearching && (
            <>
              {/* Recent searches */}
              <div>
                <p className="text-[10px] font-semibold uppercase text-gray-400 tracking-wider px-4 pt-3 pb-1">
                  Recent Searches
                </p>
                {recentSearches.map((term, idx) => {
                  const isHighlighted = highlightedIndex === idx + 1; // +1 because header is idx 0
                  return (
                    <button
                      key={term}
                      onClick={() => handleSelect({ type: "action", label: term, actionKey: `recent-${term}` })}
                      className={`w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left ${
                        isHighlighted ? "bg-indigo-50" : ""
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <i className="ri-time-line text-gray-400" />
                        {term}
                      </span>
                      <button
                        onClick={(e) => handleRemoveRecent(term, e)}
                        className="text-gray-300 hover:text-gray-500 transition-colors"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                    </button>
                  );
                })}
              </div>

              {/* Quick actions */}
              <div className="border-t border-gray-100">
                <p className="text-[10px] font-semibold uppercase text-gray-400 tracking-wider px-4 pt-3 pb-1">
                  Quick Actions
                </p>
                {quickActions.map((qa, idx) => {
                  const isHighlighted = highlightedIndex === recentSearches.length + 1 + idx + 1;
                  return (
                    <button
                      key={qa.label}
                      onClick={() => handleSelect({ type: "action", label: qa.label, actionKey: qa.action || qa.path })}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-gray-50 transition-colors text-left ${
                        isHighlighted ? "bg-indigo-50" : ""
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg ${qa.bg} flex items-center justify-center flex-shrink-0`}>
                        <i className={`${qa.icon} ${qa.color}`} />
                      </div>
                      <span className="text-gray-700 font-medium">{qa.label}</span>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {/* Results: Projects */}
          {results.projects.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase text-gray-400 tracking-wider px-4 pt-3 pb-1">
                Projects
              </p>
              {results.projects.map((proj) => {
                const projIdx = flatResults.findIndex(
                  (item) => item.type === "project" && item.data && item.data.id === proj.id
                );
                const isHighlighted = highlightedIndex === projIdx;

                return (
                  <button
                    key={`proj-${proj.id}`}
                    onClick={() => handleSelect({ type: "project", data: proj })}
                    className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-indigo-50 transition-colors text-left ${
                      isHighlighted ? "bg-indigo-50" : ""
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-building-line text-indigo-600 text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{proj.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {proj.client_name ? `Client: ${proj.client_name}` : proj.category || "Project"} · {proj.leads || 0} leads
                      </p>
                    </div>
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-700">
                      {proj.status || "Active"}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Results: Leads */}
          {results.leads.length > 0 && (
            <div className={results.projects.length > 0 ? "border-t border-gray-100" : ""}>
              <p className="text-[10px] font-semibold uppercase text-gray-400 tracking-wider px-4 pt-3 pb-1">
                Leads
              </p>
              {results.leads.map((lead) => {
                const leadIdx = flatResults.findIndex(
                  (item) => item.type === "lead" && item.data && "id" in item.data && (item.data as Lead).id === lead.id
                );
                const isHighlighted = highlightedIndex === leadIdx;
                const initials = lead.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase();

                return (
                  <button
                    key={`lead-${lead.id}`}
                    onClick={() => handleSelect({ type: "lead", data: lead })}
                    className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-indigo-50 transition-colors text-left ${
                      isHighlighted ? "bg-indigo-50" : ""
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-medium text-indigo-700">{initials}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{lead.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {lead.company} · {lead.phone}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${stageBadgeColors[lead.stage]}`}>
                        {lead.stage}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${getScoreBadgeColors(lead.score)}`}>
                        {lead.score}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Results: Calls */}
          {results.calls.length > 0 && (
            <div className={results.leads.length > 0 ? "border-t border-gray-100" : ""}>
              <p className="text-[10px] font-semibold uppercase text-gray-400 tracking-wider px-4 pt-3 pb-1">
                Calls
              </p>
              {results.calls.map((call) => {
                const callIdx = flatResults.findIndex(
                  (item) => item.type === "call" && item.data && "id" in item.data && (item.data as CallRecord).id === call.id
                );
                const isHighlighted = highlightedIndex === callIdx;

                return (
                  <button
                    key={`call-${call.id}`}
                    onClick={() => handleSelect({ type: "call", data: call })}
                    className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-indigo-50 transition-colors text-left ${
                      isHighlighted ? "bg-indigo-50" : ""
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-green-600 text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{call.leadName}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {call.date} · {call.duration === "0m" ? "Missed" : call.duration}
                      </p>
                    </div>
                    <span
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                        call.status === "Connected"
                          ? "bg-green-50 text-green-700"
                          : call.status === "Missed"
                            ? "bg-red-50 text-red-600"
                            : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {call.status}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Results: Contacts */}
          {results.contacts.length > 0 && (
            <div className={(results.leads.length > 0 || results.calls.length > 0) ? "border-t border-gray-100" : ""}>
              <p className="text-[10px] font-semibold uppercase text-gray-400 tracking-wider px-4 pt-3 pb-1">
                Contacts
              </p>
              {results.contacts.map((lead) => {
                const contactIdx = flatResults.findIndex(
                  (item) => item.type === "contact" && item.data && "id" in item.data && (item.data as Lead).id === lead.id
                );
                const isHighlighted = highlightedIndex === contactIdx;
                const initials = lead.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase();

                return (
                  <button
                    key={`contact-${lead.id}`}
                    onClick={() => handleSelect({ type: "contact", data: lead })}
                    className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-indigo-50 transition-colors text-left ${
                      isHighlighted ? "bg-indigo-50" : ""
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-medium text-gray-600">{initials}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{lead.name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {lead.phone} · {lead.company}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Results: Quotations */}
          {results.quotations.length > 0 && (
            <div className={(results.leads.length > 0 || results.calls.length > 0 || results.contacts.length > 0) ? "border-t border-gray-100" : ""}>
              <p className="text-[10px] font-semibold uppercase text-gray-400 tracking-wider px-4 pt-3 pb-1">
                Quotations
              </p>
              {results.quotations.map((qt) => {
                const qtIdx = flatResults.findIndex(
                  (item) => item.type === "quotation" && item.data && "quoteNo" in item.data && (item.data as Quotation).id === qt.id
                );
                const isHighlighted = highlightedIndex === qtIdx;

                return (
                  <button
                    key={`quotation-${qt.id}`}
                    onClick={() => handleSelect({ type: "quotation", data: qt })}
                    className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-indigo-50 transition-colors text-left ${
                      isHighlighted ? "bg-indigo-50" : ""
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-file-text-line text-indigo-600 text-sm" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{qt.quoteNo}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {qt.leadName} · {qt.leadCompany}
                      </p>
                    </div>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${quotationStatusColors[qt.status]}`}>
                      {qt.status}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}